// Draft.js
// Mirrors DraftView.swift + PlayerSelectionView.swift.
// Header → progress bar → 5 player cards → mechanic buttons → chemistry tracker.

import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import wordmark from "../assets/Rosterable.png";
import footballIcon from "../assets/football.svg";
import baseballIcon from "../assets/baseball-fill.svg";
import basketballIcon from "../assets/dribbble-logo-fill.svg";
import hockeyIcon from "../assets/hockey-fill.svg";
import soccerIcon from "../assets/soccer-ball-fill.svg";
import binocularsIcon from "../assets/binoculars-fill.svg";
import repeatIcon from "../assets/repeat-fill.svg";
import PlayerCard from "../components/PlayerCard";
import ChemistryTracker from "../components/ChemistryTracker";
import RosterPanel from "../components/RosterPanel";
import { useAuth } from "../managers/AuthContext";
import { SPORT_CONFIG, guestHasPlayedToday, recordGuestDraftPlayed, saveActiveDraft, loadActiveDraft, clearActiveDraft } from "../managers/draftManager";
import { fetchPlayersForSport, generateCandidates, displayPosition } from "../managers/playerService";
import { hasSubmittedDraftToday } from "../managers/leaderboardService";
import { loadTeamLogos } from "../managers/teamLogoService";

const SPORT_ICONS = {
  football: footballIcon,
  baseball: baseballIcon,
  basketball: basketballIcon,
  hockey: hockeyIcon,
  soccer: soccerIcon,
};

export default function Draft() {
  const navigate = useNavigate();
  const location = useLocation();
  const sport = location.state?.sport;

  useEffect(() => {
    if (!sport) navigate("/");
  }, [sport, navigate]);

  if (!sport) return null;

  return <DraftGame sport={sport} navigate={navigate} />;
}

function DraftGame({ sport, navigate }) {
  const { user, isGuest, freePlay } = useAuth();
  const config = SPORT_CONFIG[sport.id];
  const positions = config.positions;
  const totalPositions = positions.length;

  // ─── Draft limit gate ─────────────────────────────────────────────────────
  // Check if authenticated user has already submitted today for this sport.
  // Redirect home immediately if so — they can see their score on the card.
  // Free Play bypasses the gate entirely (unlimited, uncounted drafts).
  useEffect(() => {
    if (!user || isGuest || freePlay) return;
    async function checkLimit() {
      try {
        const played = await hasSubmittedDraftToday(user.uid, sport.id);
        if (played) navigate("/", { replace: true });
      } catch {
        // If check fails, allow draft to proceed
      }
    }
    checkLimit();
  }, [user, isGuest, freePlay, sport.id, navigate]);

  // ─── Draft State ─────────────────────────────────────────────────────────
  const [positionIndex, setPositionIndex] = useState(0);
  const [draftedPlayers, setDraftedPlayers] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [hasUsedReroll, setHasUsedReroll] = useState(false);
  const [hasUsedReveal, setHasUsedReveal] = useState(false);
  const [revealedIndices, setRevealedIndices] = useState(new Set());
  const [isAnimating, setIsAnimating] = useState(false);
  const [showRosterPanel, setShowRosterPanel] = useState(false);
  const [isLoadingPlayers, setIsLoadingPlayers] = useState(true);
  const [loadError, setLoadError] = useState(null);
  // Bumped once the team-logo URL map loads, so player cards re-render and
  // resolve their logos (logoURL() returns null until the map is cached).
  const [, bumpLogos] = useState(0);

  // ─── Load team logo URL map once on mount ─────────────────────────────────
  useEffect(() => {
    loadTeamLogos().then(() => bumpLogos(n => n + 1)).catch(() => {});
  }, []);

  const currentPosition = positions[positionIndex] ?? "COMPLETE";
  const excludedNames = useRef(new Set());
  const allPlayers = useRef([]);   // full sport player pool — loaded once
  const progressPct = Math.min(1, (draftedPlayers.length + 1) / totalPositions);

  // ─── Load full player pool once on mount ─────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setIsLoadingPlayers(true);
      setLoadError(null);
      try {
        const players = await fetchPlayersForSport(sport.id);
        if (!cancelled) {
          allPlayers.current = players;
          setIsLoadingPlayers(false);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to load players:", err);
          setLoadError("Couldn't load players. Check your connection.");
          setIsLoadingPlayers(false);
        }
      }
    }
    load();
    return () => { cancelled = true; };
  }, [sport.id]);

  // ─── Owner scope for persistence (uid for users, "guest" otherwise) ───────
  const ownerId = user?.uid || "guest";

  // Generate 5 fresh candidates for a slot, excluding already-drafted players.
  const makeCandidates = useCallback(
    (slot) => generateCandidates(allPlayers.current, sport.id, slot, excludedNames.current),
    [sport.id]
  );

  // Persist the in-progress draft (revealedIndices stored as a plain array).
  const persistDraft = useCallback(
    (state) => saveActiveDraft(ownerId, sport.id, state),
    [ownerId, sport.id]
  );

  // ─── Initialize: resume a saved draft, or start (and lock) a fresh one ─────
  // Runs once after the player pool loads. If a draft for this sport was
  // started today, restore it exactly — same position, same picks, same 5
  // candidates on screen. Otherwise generate position 0's candidates and save
  // immediately, so they're locked from the first frame.
  const didInit = useRef(false);
  useEffect(() => {
    if (isLoadingPlayers || allPlayers.current.length === 0 || didInit.current) return;
    didInit.current = true;

    const saved = loadActiveDraft(ownerId, sport.id);
    if (saved) {
      // Rebuild the exclusion set so future positions don't re-offer picks.
      saved.draftedPlayers.forEach((p) => excludedNames.current.add(p.name));
      setDraftedPlayers(saved.draftedPlayers);
      setPositionIndex(saved.positionIndex);
      setCandidates(saved.candidates);
      setHasUsedReroll(saved.hasUsedReroll);
      setHasUsedReveal(saved.hasUsedReveal);
      setRevealedIndices(new Set(saved.revealedIndices || []));
    } else {
      const firstCandidates = makeCandidates(positions[0]);
      setCandidates(firstCandidates);
      persistDraft({
        positionIndex: 0,
        draftedPlayers: [],
        candidates: firstCandidates,
        hasUsedReroll: false,
        hasUsedReveal: false,
        revealedIndices: [],
      });
    }
  }, [isLoadingPlayers, ownerId, sport.id, positions, makeCandidates, persistDraft]);

  // ─── Select a player ────────────────────────────────────────────────────
  function selectPlayer(index) {
    if (selectedIndex !== null || isAnimating) return;
    setSelectedIndex(index);
    const player = candidates[index];

    setTimeout(() => {
      const newDrafted = [...draftedPlayers, player];
      excludedNames.current.add(player.name);

      if (newDrafted.length >= totalPositions) {
        // Draft complete — resolve display positions, clear the saved draft,
        // and navigate.
        const resolvedPositions = positions.map((slot, i) =>
          displayPosition(slot, newDrafted[i])
        );
        clearActiveDraft(ownerId, sport.id);
        // Record guest play
        recordGuestDraftPlayed();
        navigate("/draft-complete", {
          state: { sport, draftedPlayers: newDrafted, draftPositions: resolvedPositions },
        });
      } else {
        // Advance to the next position. Generate its 5 candidates ONCE here and
        // persist immediately, so backing out restores these exact players
        // instead of re-rolling a new set.
        const nextIndex = positionIndex + 1;
        const nextCandidates = makeCandidates(positions[nextIndex]);
        setDraftedPlayers(newDrafted);
        setPositionIndex(nextIndex);
        setCandidates(nextCandidates);
        setSelectedIndex(null);
        setRevealedIndices(new Set());
        persistDraft({
          positionIndex: nextIndex,
          draftedPlayers: newDrafted,
          candidates: nextCandidates,
          hasUsedReroll,
          hasUsedReveal,
          revealedIndices: [],
        });
      }
    }, 320);
  }

  // ─── Rating Reveal mechanic ──────────────────────────────────────────────
  function handleReveal() {
    if (hasUsedReveal || isAnimating) return;
    const indices = [...candidates.keys()].sort(() => Math.random() - 0.5).slice(0, 3);
    const revealed = new Set(indices);
    setRevealedIndices(revealed);
    setHasUsedReveal(true);
    persistDraft({
      positionIndex,
      draftedPlayers,
      candidates,
      hasUsedReroll,
      hasUsedReveal: true,
      revealedIndices: [...revealed],
    });
  }

  // ─── Player Refresh mechanic ─────────────────────────────────────────────
  function handleReroll() {
    if (hasUsedReroll || isAnimating) return;
    setIsAnimating(true);
    setHasUsedReroll(true);
    setTimeout(() => {
      const newCandidates = makeCandidates(currentPosition);
      setCandidates(newCandidates);
      setSelectedIndex(null);
      setRevealedIndices(new Set());
      setIsAnimating(false);
      persistDraft({
        positionIndex,
        draftedPlayers,
        candidates: newCandidates,
        hasUsedReroll: true,
        hasUsedReveal,
        revealedIndices: [],
      });
    }, 150);
  }

  return (
    <div style={styles.container}>
      {/* ── Header ── */}
      <div style={styles.header}>
        <button style={styles.backBtn} onClick={() => navigate("/")}>
          ‹
        </button>
        <img src={wordmark} alt="Rosterable" style={styles.wordmark} />
        <button
          style={styles.rosterBtn}
          onClick={() => setShowRosterPanel(true)}
        >
          <span style={styles.rosterIcon}>☰</span>
          {draftedPlayers.length > 0 && (
            <span style={{ ...styles.rosterBadge, background: sport.hex }}>
              {draftedPlayers.length}
            </span>
          )}
        </button>
      </div>

      {/* ── Progress section ── */}
      <div style={styles.progressSection}>
        <div style={styles.progressRow}>
          <div style={{ ...styles.sportCircle, background: sport.hex }}>
            <img src={SPORT_ICONS[sport.id]} alt={sport.name} style={styles.sportCircleIcon} />
          </div>
          <div style={styles.draftingLabel}>
            <span style={styles.draftingText}>DRAFTING</span>
            <span style={{ ...styles.positionLabel, color: sport.hex }}>
              {currentPosition}
            </span>
          </div>
          <span style={styles.countLabel}>
            {Math.min(draftedPlayers.length + 1, totalPositions)} OF {totalPositions}
          </span>
        </div>
        <div style={styles.progressBarTrack}>
          <div
            style={{
              ...styles.progressBarFill,
              width: `${progressPct * 100}%`,
              background: `linear-gradient(to right, ${sport.hex}, ${sport.hex}cc)`,
              boxShadow: `0 0 6px ${sport.hex}66`,
              transition: "width 0.3s ease",
            }}
          />
        </div>
      </div>

      {/* ── Player Cards or Loading/Error State ── */}
      <div style={styles.cardsSection}>
        {isLoadingPlayers ? (
          <div style={styles.loadingState}>
            <span style={styles.loadingText}>Loading players...</span>
          </div>
        ) : loadError ? (
          <div style={styles.loadingState}>
            <span style={styles.loadingText}>{loadError}</span>
            <button style={styles.retryBtn} onClick={() => window.location.reload()}>
              RETRY
            </button>
          </div>
        ) : (
          candidates.map((player, index) => (
            <PlayerCard
              key={index}
              player={player}
              sport={sport}
              position={sport.id === "football"
                ? displayPosition(currentPosition, player)
                : currentPosition}
              isSelected={selectedIndex === index}
              showRating={revealedIndices.has(index)}
              onTap={() => selectPlayer(index)}
              isDisabled={selectedIndex !== null && selectedIndex !== index}
            />
          ))
        )}
      </div>

      {/* ── Mechanic Buttons ── */}
      <div style={styles.mechanicsRow}>
        <MechanicButton
          label="RATING REVEAL"
          iconSrc={binocularsIcon}
          isUsed={hasUsedReveal}
          sportColor={sport.hex}
          onClick={handleReveal}
        />
        <MechanicButton
          label="PLAYER REFRESH"
          iconSrc={repeatIcon}
          isUsed={hasUsedReroll}
          sportColor={sport.hex}
          onClick={handleReroll}
        />
      </div>

      {/* ── Chemistry Tracker ── */}
      <ChemistryTracker players={draftedPlayers} sport={sport} />

      {/* ── Mid-draft Roster Panel ── */}
      {showRosterPanel && (
        <RosterPanel
          sport={sport}
          draftedPlayers={draftedPlayers}
          positions={positions}
          onClose={() => setShowRosterPanel(false)}
        />
      )}
    </div>
  );
}

// ─── Mechanic Button ──────────────────────────────────────────────────────────

function MechanicButton({ label, iconSrc, isUsed, sportColor, onClick }) {
  const [isPressed, setIsPressed] = useState(false);
  return (
    <button
      style={{
        ...styles.mechanicBtn,
        opacity: isUsed ? 0.4 : 1,
        background: isUsed ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.08)",
        borderColor: isUsed ? "rgba(255,255,255,0.1)" : `${sportColor}4d`,
        transform: isPressed && !isUsed ? "scale(0.98)" : "scale(1)",
        transition: "all 0.1s ease",
        cursor: isUsed ? "default" : "pointer",
      }}
      onClick={isUsed ? undefined : onClick}
      onMouseDown={() => { if (!isUsed) setIsPressed(true); }}
      onMouseUp={() => setIsPressed(false)}
      onMouseLeave={() => setIsPressed(false)}
    >
      {!isUsed && iconSrc && (
        <img src={iconSrc} alt="" style={styles.mechanicIcon} />
      )}
      <span style={{
        ...styles.mechanicLabel,
        color: isUsed ? "rgba(255,255,255,0.4)" : "#ffffff",
      }}>
        {isUsed ? "✓ USED" : label}
      </span>
    </button>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = {
  container: {
    minHeight: "100vh",
    width: "100%",
    maxWidth: "480px",
    margin: "0 auto",
    padding: "0 16px 32px",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingTop: "52px",
    paddingBottom: "16px",
  },
  backBtn: {
    background: "none",
    color: "rgba(255,255,255,0.8)",
    fontSize: "28px",
    fontWeight: "300",
    lineHeight: 1,
    padding: "4px 8px 4px 0",
    width: "44px",
  },
  wordmark: { height: "21px", width: "auto" },
  rosterBtn: {
    background: "none",
    position: "relative",
    width: "44px",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
  },
  rosterIcon: {
    fontSize: "20px",
    color: "rgba(255,255,255,0.8)",
  },
  rosterBadge: {
    position: "absolute",
    top: "-6px",
    right: "-4px",
    width: "18px",
    height: "18px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "10px",
    fontWeight: "800",
    color: "#000",
  },
  progressSection: {
    paddingBottom: "20px",
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  progressRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  sportCircle: {
    width: "44px",
    height: "44px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    // Shift down so the top of the logo lines up with the top of "DRAFTING".
    // transform doesn't affect layout, so nothing below it moves. Tune this
    // single value if the alignment needs nudging.
    transform: "translateY(13px)",
  },
  sportCircleIcon: {
    width: "26px",
    height: "26px",
    filter: "brightness(0)",
  },
  draftingLabel: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    flex: 1,
  },
  draftingText: {
    fontSize: "17px",
    fontWeight: "500",
    color: "rgba(255,255,255,0.7)",
    letterSpacing: "0.5px",
  },
  positionLabel: {
    fontSize: "17px",
    fontWeight: "800",
    letterSpacing: "1px",
  },
  countLabel: {
    fontSize: "15px",
    fontWeight: "600",
    color: "rgba(255,255,255,0.6)",
    letterSpacing: "1px",
  },
  progressBarTrack: {
    height: "8px",
    borderRadius: "4px",
    background: "rgba(255,255,255,0.08)",
    overflow: "hidden",
    marginLeft: "56px",
  },
  progressBarFill: {
    height: "100%",
    borderRadius: "4px",
  },
  loadingState: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "16px",
    padding: "40px 0",
  },
  loadingText: {
    fontSize: "14px",
    color: "rgba(255,255,255,0.4)",
    fontWeight: "500",
  },
  retryBtn: {
    padding: "8px 20px",
    borderRadius: "8px",
    border: "1px solid rgba(255,255,255,0.2)",
    background: "transparent",
    color: "rgba(255,255,255,0.6)",
    fontSize: "12px",
    fontWeight: "700",
    letterSpacing: "1px",
    cursor: "pointer",
  },
  cardsSection: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    paddingTop: "8px",
  },
  mechanicsRow: {
    display: "flex",
    gap: "12px",
    paddingTop: "10px",
    paddingBottom: "8px",
  },
  mechanicBtn: {
    flex: 1,
    height: "44px",
    borderRadius: "10px",
    border: "1px solid",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "7px",
  },
  mechanicIcon: {
    width: "16px",
    height: "16px",
    filter: "brightness(0) invert(1)",
    flexShrink: 0,
  },
  mechanicLabel: {
    fontSize: "13px",
    fontWeight: "600",
    letterSpacing: "0.5px",
  },
};
